import React, { useState, useEffect, useCallback } from 'react';
import WrapperComponent from '../commonComponents/WrapperComponent';
import PaginationComponent from '../commonComponents/PaginationComponent';
import SearchBox from './SearchBox';
import GroupTable from './GroupTable';
import DeleteNoModal from './DeleteNoModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import { useNavigate } from 'react-router-dom';
import { url } from '../../util/config';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ManageContactsComponent = () => {
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [mobileNumber, setMobileNumber] = useState('');
    const [mobileError, setMobileError] = useState('');
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const itemsPerPage = 5;
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Single reusable fetchGroups
    const fetchGroups = useCallback(async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await fetch(`${url}/contacts`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to fetch groups');
            const data = await response.json();
            setGroups(data);
        } catch (err) {
            console.error('Error fetching groups:', err);
            setError('Failed to load contact groups. Please try again later.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => { fetchGroups(); }, [fetchGroups]);

    const filteredGroups = groups.filter((group) =>
        group.group_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentGroups = filteredGroups.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);

    const handleSearchTermChange = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    };

    const handleDeleteGroup = async () => {
        if (!groupToDelete) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/contacts/${groupToDelete}`, {
                method: 'DELETE',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (response.ok) {
                const data = await response.json();
                // Backend now returns message key (fixed)
                toast.success(data.message || 'Group deleted successfully!', { autoClose: 3000 });
                await fetchGroups();
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to delete the group. Please try again.', { autoClose: 3000 });
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 3000 });
        } finally {
            setShowConfirmModal(false);
            setGroupToDelete(null);
        }
    };

    const handleDownload = async (groupId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/contacts/download/${groupId}`, {
                method: 'GET',
                headers: { 'Authorization': 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to download the file');
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = `group_${groupId}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success('File downloaded successfully!', { autoClose: 3000 });
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Failed to download the file. Please try again.', { autoClose: 3000 });
        }
    };

    const handleDeleteNo = async (contactId) => {
        const indianMobileRegex = /^[6-9]\d{9}$/;
        if (!indianMobileRegex.test(mobileNumber)) {
            setMobileError('Please enter a valid 10-digit Indian mobile number.');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/contacts/deleteno`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
                body: JSON.stringify({ contactid: contactId, mobileno: mobileNumber }),
            });
            if (response.ok) {
                await fetchGroups();
                setShowModal(false);
                setMobileNumber('');
                setMobileError('');
                toast.success('Mobile number deleted successfully!', { autoClose: 3000 });
            } else {
                const errorData = await response.json();
                setMobileError(errorData.error || 'Failed to delete the mobile number.');
            }
        } catch (error) {
            console.error('Error deleting mobile number:', error);
            setMobileError('An error occurred. Please try again.');
        }
    };

    const openConfirmModal = (groupId) => {
        setGroupToDelete(groupId);
        setShowConfirmModal(true);
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                {/* Page header */}
                <div style={styles.pageHeader}>
                    <div>
                        <h5 style={styles.pageTitle}>Manage Contacts</h5>
                        <p style={styles.pageSubtitle}>View and manage your contact groups</p>
                    </div>
                </div>

                {/* Search + Add button */}
                <SearchBox
                    searchTerm={searchTerm}
                    setSearchTerm={handleSearchTermChange}
                    navigate={navigate}
                />

                {/* Table card */}
                <div style={styles.card}>
                    {isLoading ? (
                        <div style={styles.centerState}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p style={styles.stateText}>Loading contact groups…</p>
                        </div>
                    ) : error ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-exclamation-circle" style={{ fontSize: 32, color: '#ef4444' }}></i>
                            <p style={{ ...styles.stateText, color: '#ef4444' }}>{error}</p>
                            <button style={styles.btnPrimary} onClick={fetchGroups}>Retry</button>
                        </div>
                    ) : filteredGroups.length === 0 ? (
                        <div style={styles.centerState}>
                            <i className="bi bi-people" style={{ fontSize: 36, color: '#d1d5db' }}></i>
                            <p style={styles.stateText}>
                                {searchTerm ? 'No groups match your search.' : 'No contact groups found. Create your first group.'}
                            </p>
                            {!searchTerm && (
                                <button style={styles.btnPrimary} onClick={() => navigate('/createContactGroup')}>
                                    Add Contact Group
                                </button>
                            )}
                        </div>
                    ) : (
                        <GroupTable
                            currentGroups={currentGroups}
                            navigate={navigate}
                            setShowModal={setShowModal}
                            setGroupToDelete={setGroupToDelete}
                            openConfirmModal={openConfirmModal}
                            handleDownload={handleDownload}
                        />
                    )}
                </div>

                {/* Pagination */}
                {!isLoading && !error && filteredGroups.length > 0 && (
                    <PaginationComponent
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(page) => setCurrentPage(page)}
                    />
                )}

                <ToastContainer />

                {showModal && (
                    <DeleteNoModal
                        mobileNumber={mobileNumber}
                        setMobileNumber={setMobileNumber}
                        errorMessage={mobileError}
                        setErrorMessage={setMobileError}
                        handleDeleteNo={() => handleDeleteNo(groupToDelete)}
                        setShowModal={setShowModal}
                    />
                )}
                {showConfirmModal && (
                    <ConfirmDeleteModal
                        setShowConfirmModal={setShowConfirmModal}
                        handleDeleteGroup={handleDeleteGroup}
                    />
                )}
            </div>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', flexDirection: 'column', gap: '16px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' },
    pageTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: '13px', color: '#6b7280' },
    card: { background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', padding: '60px 20px' },
    stateText: { fontSize: '14px', color: '#6b7280', margin: 0 },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default ManageContactsComponent;
